/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  getTotalBudgetString,
} from '@marageti/z4-lib';
import { TripMatch } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { titleCase } from '../../../utils/helpers';
import { formatCountryCodesToString, getFirstNameAndLastInitial } from '../utils';
import { TripMatchTravelerChips, RefineSoldChip } from '../chips';
import { ChatIndicator } from '../trip-indicators';

const {
  OutlinedUserGroupIcon,
  OutlinedLocationMarkerIcon,
  OutlinedCurrencyDollarIcon,
  OutlinedUserIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);

type SoldCardViewProps = {
  countryCodes: CountryCodeMap | undefined;
  tripMatch: TripMatch;
};

const SoldCardView = ({
  countryCodes,
  tripMatch,
}: SoldCardViewProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const fullName = titleCase(tripMatch?.travelers[0].fullName);
  const countryNames = countryCodes ? formatCountryCodesToString(tripMatch.countries, countryCodes) : '';
  const departureDate = getFormattedDateShortMonthDDYYYY(tripMatch.departureDate) || '';
  const totalNights = `${tripMatch.totalNights} Nights`;
  const numberOfTravelers = tripMatch.numberOfAdults + tripMatch.numberOfChildren;
  const totalTravelers = numberOfTravelers === 1
    ? '1 Traveler'
    : `${numberOfTravelers} Travelers`;
  const assignedToFirstNameLastInitial = getFirstNameAndLastInitial(tripMatch.assignedToName);
  const matchedDate = getFormattedDateShortMonthDDYYYY(tripMatch.createdAt) || '';

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={`/trip-matches/${tripMatch?.id}`}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1}>
              <Typography variant="bodyBold">
                {fullName}
              </Typography>
              <TripMatchTravelerChips tripMatch={tripMatch} />
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedUserGroupIcon} label={totalTravelers} />
              <IconLabel icon={OutlinedLocationMarkerIcon} label={countryNames} />
              <IconLabel
                icon={DepartureIcon}
                label={(
                  <>
                    {departureDate}
                    {' '}
                    ·
                    {' '}
                    {totalNights}
                  </>
                )}
              />
              <IconLabel
                icon={OutlinedCurrencyDollarIcon}
                label={(
                  <>
                    {getTotalBudgetString(tripMatch.totalTripPrice.amount, 2, 2, tripMatch.totalTripPrice.currencyCode)}
                    {' '}
                    Total
                  </>
                )}
              />
              <IconLabel icon={OutlinedUserIcon} label={assignedToFirstNameLastInitial} />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                <RefineSoldChip tripMatch={tripMatch} />
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <ChatIndicator tripMatch={tripMatch} />
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1}>
            <Grid item xs={3}>
              <Stack direction="row" spacing={1}>
                <Typography variant="bodyBold">
                  {fullName}
                </Typography>
                <TripMatchTravelerChips tripMatch={tripMatch} />
              </Stack>
              <Typography variant="caption">
                {countryNames}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {departureDate}
              </Typography>
              <Typography variant="caption">
                {totalNights}
                {' '}
                ·
                {' '}
                {totalTravelers}
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {getTotalBudgetString(tripMatch.totalTripPrice.amount, 2, 2, tripMatch.totalTripPrice.currencyCode)}
              </Typography>
              <Typography variant="caption">
                Total Trip Price
              </Typography>
            </Grid>

            <Grid item xs={2}>
              <Typography variant="body">
                {assignedToFirstNameLastInitial}
              </Typography>
              <Typography variant="caption">
                Matched:
                {' '}
                {matchedDate}
              </Typography>
            </Grid>

            <Grid item xs={1.5}>
              <Stack direction="row" justifyContent="center" spacing={3}>
                <ChatIndicator tripMatch={tripMatch} />
              </Stack>
            </Grid>
            <Grid item xs={1.5} display="flex" justifyContent="flex-end">
              <RefineSoldChip tripMatch={tripMatch} />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default SoldCardView;
